<template>
  <BaseScreen v-bind="screen">
    <BaseMenu
      :items="menuItems"
    />
    <ModalConfirm
      v-if="isAccountModalVisible"
      v-bind="accountModal"
      class="screen__overlay"
      slot="root"
      @disagree="hideAccountModal"
      @agree="goToAccountsScreen"
    />
    <div class="screen__logout" slot="actions">
      <BaseTitle
        v-if="user"
        v-bind="user"
        class="screen__username"
      />
      <BaseButton
        v-if="logoutButton"
        v-bind="logoutButton"
        @click="logout"
      />
    </div>
  </BaseScreen>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import CONSTANTS from '@/helpers/constants';

import screenMixin from '@/mixins/screen';
import BaseMenu from '@/components/BaseMenu.vue';
import BaseTitle from '@/components/BaseTitle.vue';
import BaseButton from '@/components/BaseButton.vue';
import ModalConfirm from '@/components/ModalConfirm.vue';

export default {
  name: 'PersonalScreen',
  mixins: [screenMixin],
  components: {
    BaseMenu,
    BaseTitle,
    BaseButton,
    ModalConfirm,
  },
  data() {
    return {
      isAccountModalVisible: false,
    };
  },
  computed: {
    ...mapState('personal', [
      'login',
    ]),
    ...mapGetters('personal', [
      'hasAccount',
    ]),
    screenCase() {
      if (this.isAccountModalVisible) {
        return CONSTANTS.STEP_CONFIRM_CREATE;
      }
      return CONSTANTS.STEP_PERSONAL;
    },
    menuItems() {
      return [
        {
          icon: {
            name: 'meter',
            theme: 'plain',
            size: 's',
          },
          label: {
            text: this.$t('actions.sendInfo'),
            size: 'm',
            isBright: true,
            isNormal: true,
          },
          action: this.goToMeterScreen,
          disabled: !this.hasAccount,
        },
        {
          icon: {
            name: 'user',
            theme: 'plain',
            size: 's',
          },
          label: {
            text: this.$t('actions.editProfile'),
            size: 'm',
            isBright: true,
            isNormal: true,
          },
          action: this.goToProfileScreen,
        },
        {
          icon: {
            name: 'accounts',
            theme: 'plain',
            size: 's',
          },
          label: {
            text: this.$t('actions.editAccounts'),
            size: 'm',
            isBright: true,
            isNormal: true,
          },
          action: this.goToAccountsScreen,
        },
        {
          icon: {
            name: 'pay',
            theme: 'plain',
            size: 's',
          },
          label: {
            text: this.$t('actions.showBalance'),
            size: 'm',
            isBright: true,
            isNormal: true,
          },
          action: this.goToBalanceScreen,
          disabled: !this.hasAccount,
        },
        {
          icon: {
            name: 'home',
            theme: 'plain',
            size: 's',
          },
          label: {
            text: this.$t('actions.home'),
            size: 'm',
            isBright: true,
            isNormal: true,
          },
          action: this.goToHomeScreen,
        },
      ];
    },
    user() {
      return {
        text: this.login,
      };
    },
    logoutButton() {
      return {
        text: this.$t('actions.exit'),
        view: 'outlined',
      };
    },
    accountModal() {
      return {
        title: {
          text: this.$t('titles.needAccount'),
          size: 'l',
          isNormal: true,
          isCenter: true,
        },
        agreeText: this.$t('actions.addAccount'),
        disagreeText: this.$t('actions.skipAddAccount'),
      };
    },
  },
  beforeRouteLeave(to, from, next) {
    const isToScreenNeedAccount = [
      this.$router.options.paths.personalMeter,
      this.$router.options.paths.personalBalance,
    ].includes(to.path);
    if (isToScreenNeedAccount && !this.hasAccount) {
      this.showAccountModal();
    } else {
      next();
    }
  },
  created() {
    this.initActions([
      [CONSTANTS.ACTION_EDIT_PROFILE, this.goToProfileScreen],
      [CONSTANTS.ACTION_SHOW_ACCOUNTS, this.goToAccountsScreen],
      [CONSTANTS.ACTION_SEND_METER, this.goToMeterScreen],
      [CONSTANTS.ACTION_PAY, this.goToBalanceScreen],
      [CONSTANTS.ACTION_LOGOUT, this.logout],
      [CONSTANTS.ACTION_DISAGREE, this.hideAccountModal],
      [CONSTANTS.ACTION_AGREE, this.goToAccountsScreen],
    ]);
  },
  methods: {
    ...mapActions('personal', [
      'clearAll',
    ]),
    logout() {
      this.clearAll();
      this.goToHomeScreen();
    },
    showAccountModal() {
      this.isAccountModalVisible = true;
    },
    hideAccountModal() {
      this.isAccountModalVisible = false;
    },
    goToMeterScreen() {
      this.$router.push(this.$router.options.paths.personalMeter);
    },
    goToProfileScreen() {
      this.$router.push(this.$router.options.paths.personalProfile);
    },
    goToAccountsScreen() {
      this.$router.push(this.$router.options.paths.personalAccounts);
    },
    goToBalanceScreen() {
      this.$router.push(this.$router.options.paths.personalBalance);
    },
    onScreenReady() {
      if (this.hasAccount) return;
      this.showAccountModal();
    },
  },
};
</script>
